import React, {
  createContext,
  ReactNode,
  useContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLocalStorage } from "@uidotdev/usehooks";
import { get } from "lodash-es";
import { useSafeState } from "hooks/useSafeState";
import { useFirebase } from "firebase";

import { FormConfig, Screen } from "./types";
import { substituteVariables } from "./variables";
import { getRelevantFieldRefs } from "./logic";

// -----------------------
// Type Definitions
// -----------------------

/**
 * Defines the shape of the FormConfig context.
 */
interface FormConfigContextProps {
  config: FormConfig;
  variables: Record<string, unknown>;
  context: Record<string, unknown>;
  responseId: string;
}

/**
 * Creates a React context for FormConfig.
 * The default value is undefined to enforce the usage of a Provider.
 */
const FormConfigContext = createContext<FormConfigContextProps | undefined>(
  undefined,
);

// -----------------------
// Provider Component
// -----------------------

interface FormConfigProviderProps {
  config: FormConfig;
  variables: Record<string, unknown>;
  context: Record<string, unknown>;
  responseId: string;
  children: ReactNode;
}

/**
 * FormConfigProvider component that supplies the FormConfig to its children.
 *
 * @param config - The form configuration to provide.
 * @param children - The child components that will consume the form configuration.
 */
export const FormConfigProvider: React.FC<FormConfigProviderProps> = ({
  config,
  variables,
  context,
  responseId,
  children,
}) => {
  const contextValue = { config, variables, context, responseId };

  return (
    <FormConfigContext.Provider value={contextValue}>
      {children}
    </FormConfigContext.Provider>
  );
};

/**
 * Custom hook to consume the FormConfig context.
 *
 * @returns The current FormConfig.
 * @throws Error if used outside of a FormConfigProvider.
 */
export const useFormConfig = (): FormConfigContextProps => {
  const context = useContext(FormConfigContext);
  if (!context) {
    throw new Error("useFormConfig must be used within a FormConfigProvider");
  }
  return context;
};

export const useFormTextSubstitution = () => {
  // Field components also render outside the form engine (e.g. the
  // arrangement flow's delivery address); with no provider there are no
  // variables to substitute, so fall back to identity.
  const formConfig = useContext(FormConfigContext);

  function sub(input: string): string {
    if (!formConfig) return input;
    return substituteVariables(input, {
      variables: formConfig.variables,
      context: formConfig.context,
    });
  }

  return { substituteVariables: sub };
};

export function useFormContext(): Record<string, unknown> {
  const { state } = useLocation();

  if (state) {
    return state.form_context;
  }

  return {};
}

export function buildFormContext(
  formId: string,
  baseContext: Record<string, unknown>,
  queryParams: Record<string, string>,
): Record<string, unknown> {
  if (
    formId === "pre-need-application" &&
    Object.prototype.hasOwnProperty.call(queryParams, "turbo")
  ) {
    return { ...baseContext, turbo: "1" };
  }

  return baseContext;
}

type AutoRedirect = NonNullable<Screen["properties"]>["auto_redirect"];

const REDIRECT_VARIABLE_PATTERN =
  /{{\s*(var|context)\s*:\s*([a-zA-Z0-9_.]+)\s*}}/g;

function hasMissingRedirectSubstitution(
  path: string,
  substitutionContext: {
    variables: Record<string, unknown>;
    context: Record<string, unknown>;
  },
): boolean {
  REDIRECT_VARIABLE_PATTERN.lastIndex = 0;
  let match = REDIRECT_VARIABLE_PATTERN.exec(path);

  while (match) {
    const [, prefix, variablePath] = match;
    const source =
      prefix === "var"
        ? substitutionContext.variables
        : substitutionContext.context;
    const value = get(source, variablePath);

    if (value === undefined || value === null) {
      return true;
    }

    match = REDIRECT_VARIABLE_PATTERN.exec(path);
  }

  return false;
}

function mergeSearchParams(path: string, search: string): string {
  const sourceParams = new URLSearchParams(search);
  if (!Array.from(sourceParams).length) return path;

  const hashIndex = path.indexOf("#");
  const pathWithoutHash = hashIndex === -1 ? path : path.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : path.slice(hashIndex);
  const queryIndex = pathWithoutHash.indexOf("?");
  const basePath =
    queryIndex === -1 ? pathWithoutHash : pathWithoutHash.slice(0, queryIndex);
  const existingSearch =
    queryIndex === -1 ? "" : pathWithoutHash.slice(queryIndex + 1);
  const mergedParams = new URLSearchParams(existingSearch);

  sourceParams.forEach((value, key) => {
    mergedParams.append(key, value);
  });

  const query = mergedParams.toString();
  return `${basePath}${query ? `?${query}` : ""}${hash}`;
}

function resolveAutoRedirectPath(
  redirect: AutoRedirect | undefined,
  {
    context = {},
    variables = {},
    search = "",
  }: {
    context?: Record<string, unknown>;
    variables?: Record<string, unknown>;
    search?: string;
  },
) {
  if (!redirect) return undefined;

  const substitutionContext = { variables, context };
  if (hasMissingRedirectSubstitution(redirect.path, substitutionContext)) {
    return undefined;
  }

  const path = substituteVariables(redirect.path, substitutionContext);

  return redirect.preserve_search_params
    ? mergeSearchParams(path, search)
    : path;
}

export function useFormRedirect({
  formConfig,
  formContext = {},
  variables = {},
}: {
  formConfig: FormConfig;
  formContext?: Record<string, unknown>;
  variables?: Record<string, unknown>;
}) {
  const { user: firebaseUser } = useFirebase();
  const location = useLocation();
  const navigate = useNavigate();

  const getRedirectPath = useCallback(
    (redirect: AutoRedirect | undefined) => {
      if (!redirect) return undefined;
      if (redirect.type === "authenticated_user" && !firebaseUser) {
        return undefined;
      }

      return resolveAutoRedirectPath(redirect, {
        context: { ...(formConfig.context ?? {}), ...formContext },
        variables,
        search: location.search,
      });
    },
    [firebaseUser, formConfig.context, formContext, location.search, variables],
  );

  const scheduleRedirect = useCallback(
    (redirect: AutoRedirect | undefined) => {
      const redirectPath = getRedirectPath(redirect);
      if (!redirectPath) return undefined;

      const timeoutId = window.setTimeout(
        () => navigate(redirectPath, { replace: true }),
        redirect?.delay_ms ?? 3000,
      );

      return () => window.clearTimeout(timeoutId);
    },
    [getRedirectPath, navigate],
  );

  return { getRedirectPath, scheduleRedirect };
}

interface UseFormProgressProps {
  formConfig: FormConfig;
  currentFieldRef: string | null;
  currentEndScreenRef: string | null;
}

export function useFormProgress({
  formConfig,
  currentFieldRef,
  currentEndScreenRef,
}: UseFormProgressProps) {
  const [visitedFields, setVisitedFields] = useState<string[]>([]);

  useEffect(() => {
    if (currentFieldRef && !visitedFields.includes(currentFieldRef)) {
      setVisitedFields((prev) => [...prev, currentFieldRef]);
    }
  }, [currentFieldRef, visitedFields]);

  const progress = useMemo(() => {
    if (currentEndScreenRef) return 100;
    if (!currentFieldRef) return 0;

    // Get all possible remaining fields from current position
    const remainingFieldRefs = getRelevantFieldRefs(
      formConfig,
      {}, // empty context since we're exploring all paths
      {}, // empty form data to explore all possible paths
      currentFieldRef, // Start from current field
    );

    // Total path length is visited fields + remaining possible fields
    const maxPathLength = visitedFields.length + remainingFieldRefs.length;

    // Current progress is based on number of visited fields
    const currentProgress = (visitedFields.length / maxPathLength) * 100;

    return Math.min(currentProgress, 99);
  }, [currentFieldRef, visitedFields, formConfig, currentEndScreenRef]);

  return progress;
}

/**
 * Helper function to safely parse localStorage value
 */
function parseLocalStorageValue(key: string, defaultValue: any = {}) {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
}

/**
 * Validates the structure of stored form data
 * Returns true if data is valid, false if corrupted/invalid
 */
function isValidStoredFormData(data: any): boolean {
  // Check if data exists and is an object
  if (!data || typeof data !== "object") {
    return false;
  }

  // Check formData exists and is an object (or undefined for new users)
  if (data.formData !== undefined && typeof data.formData !== "object") {
    return false;
  }

  // Check formState exists and is an object
  if (!data.formState || typeof data.formState !== "object") {
    return false;
  }

  const { formState } = data;

  // Validate formState properties have correct types
  if (
    typeof formState.isReviewMode !== "boolean" ||
    typeof formState.returnToReview !== "boolean" ||
    !Array.isArray(formState.fieldHistory) ||
    !formState.fieldHistory.every((item: any) => typeof item === "string")
  ) {
    return false;
  }

  return true;
}

// ====================================================================
// MIGRATION CODE - Remove after 6 months
// ====================================================================

/**
 * Migrates old localStorage keys to new format
 * Returns migrated data and cleans up old keys
 */
function migrateOldFormData(
  formId: string,
  responseId: string,
  instanceHash?: string,
) {
  const oldDataKey = `afd_${formId}_${responseId}`;

  // Get old form data (only this key was actually deployed)
  const oldFormData = parseLocalStorageValue(oldDataKey, {});

  // Clean up old key
  localStorage.removeItem(oldDataKey);

  return {
    formData: oldFormData,
    formState: {
      isReviewMode: false,
      returnToReview: false,
      fieldHistory: [],
      instanceHash,
    },
  };
}

// ====================================================================
// END MIGRATION CODE
// ====================================================================

/**
 * Combined hook for form data and state persistence with backwards compatibility
 */
export function useFormStorage(
  formId: string,
  responseId: string,
  formConfig: FormConfig,
) {
  const storageKey = `af_${formId}_${responseId}`;
  const showReview = formConfig.properties?.show_review;
  const instanceHash = formConfig.instance_hash;

  // Get initial data - only runs once during initialization
  const getInitialData = useMemo(() => {
    const defaultState = {
      formData: {},
      formState: {
        isReviewMode: false,
        returnToReview: false,
        fieldHistory: [],
        instanceHash,
      },
    };

    // Check if new format already exists
    const existingData = parseLocalStorageValue(storageKey, null);
    if (existingData) {
      // Validate structure first
      if (!isValidStoredFormData(existingData)) {
        console.log("Clearing corrupted localStorage data");
        localStorage.removeItem(storageKey);
        return defaultState;
      }

      const { formState } = existingData;

      // Check revision compatibility
      if (instanceHash && formState.instanceHash !== instanceHash) {
        console.log(
          `Form revision changed (${formState.instanceHash} → ${instanceHash}), starting fresh to prevent compatibility issues`,
        );
        localStorage.removeItem(storageKey);
        return defaultState;
      }

      // If show_review is false, preserve form data but reset navigation state
      if (!showReview) {
        console.log(
          "Form state restoration disabled: show_review is not enabled. Preserving form data only.",
        );
        return {
          formData: existingData.formData, // Keep user's answers
          formState: {
            isReviewMode: false,
            returnToReview: false,
            fieldHistory: [],
            instanceHash,
          },
        };
      }

      // All checks passed - return the data as-is
      return existingData;
    }

    // ====================================================================
    // MIGRATION CODE - Remove this block after 6 months
    // ====================================================================
    const migratedData = migrateOldFormData(formId, responseId, instanceHash);
    return migratedData;
    // ====================================================================
    // Replace above return with this after migration period:
    // return defaultState;
    // ====================================================================
  }, [storageKey, showReview, instanceHash, formId, responseId]);

  const [storedForm, setStoredForm] = useLocalStorage(
    storageKey,
    getInitialData,
  );

  const [formData, setFormData] = useSafeState(storedForm.formData || {});

  // Initialize form data state once
  useEffect(() => {
    setFormData({ ...storedForm.formData });
  }, []);

  const updateFormData = (data: Record<string, any>) => {
    setStoredForm((prev: any) => ({
      ...prev,
      formData: data,
    }));
  };

  const updateFormState = (state: {
    isReviewMode: boolean;
    returnToReview: boolean;
    fieldHistory: string[];
  }) => {
    // Don't save form state if show_review is disabled
    if (!showReview) {
      return;
    }

    setStoredForm((prev: any) => ({
      ...prev,
      formState: {
        ...state,
        instanceHash, // Always include current revision ID
      },
    }));
  };

  const clearAll = () => {
    setStoredForm({
      formData: {},
      formState: {
        isReviewMode: false,
        returnToReview: false,
        fieldHistory: [],
        instanceHash,
      },
    });
  };

  return {
    formData,
    formState: storedForm.formState || {
      isReviewMode: false,
      returnToReview: false,
      fieldHistory: [],
      instanceHash,
    },
    updateFormData,
    updateFormState,
    clearAll,
  };
}
